import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { computeLayout, safeValidateTimeline, type Timeline } from "@veyso/core";
import { api } from "../api.js";
import { isTypingInto } from "../lib/keyboard.js";

export type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";

const HISTORY_LIMIT = 60;
const AUTOSAVE_DEBOUNCE_MS = 600;

/**
 * Editor state for one project's timeline.
 *
 * Three things have to hold at once: the preview must always render a *valid*
 * timeline, undo must be instant, and the server must not be asked to store a
 * revision on every mouse move. So edits are validated before they are applied,
 * history is kept client-side, and saving is debounced — a drag produces one
 * revision, not sixty.
 */
export function useTimelineEditor(projectId: string, previewFps?: number) {
  const [timeline, setTimelineState] = useState<Timeline | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const past = useRef<Timeline[]>([]);
  const future = useRef<Timeline[]>([]);
  /**
   * The timeline as the history sees it, so an edit can be computed without
   * reaching into a state updater.
   *
   * The three mutators used to change `past` and `future` *inside*
   * `setTimelineState`, which is a function React is entitled to call more than
   * once — and does, in development — so the history could be popped twice for
   * one undo. It never misbehaved in testing, but a stack that depends on an
   * updater running exactly once is a stack that will eventually be wrong.
   */
  const currentRef = useRef<Timeline | null>(null);
  /**
   * How deep the history is, in state rather than derived from the refs.
   *
   * A ref read during render is not a subscription: the buttons would show
   * whatever the depth happened to be at the last unrelated re-render. This is
   * what tells them whether they can do anything.
   */
  const [depth, setDepth] = useState({ past: 0, future: 0 });
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<Timeline | null>(null);
  /**
   * Set while the server is rebuilding this timeline from a cut.
   *
   * An edit made then is ignored rather than queued. Saved, it would race the
   * rebuild and whichever landed last would win, leaving the timeline out of
   * step with the cut plan; held, it would be thrown away by the reload anyway.
   * A ref, so suspending changes nothing about the functions handed to every
   * pane.
   */
  const suspended = useRef(false);
  const suspendEdits = useCallback((on: boolean) => {
    suspended.current = on;
  }, []);

  const load = useCallback(async () => {
    try {
      const response = await api.timeline(projectId);
      const parsed = safeValidateTimeline(response.data);
      if (!parsed.success) {
        setLoadError("The stored timeline is not valid. Re-run processing to rebuild it.");
        return;
      }
      // Anything queued locally is stale now — the server has just rebuilt this
      // timeline, and flushing the old one would undo the rebuild.
      pending.current = null;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      past.current = [];
      future.current = [];
      setDepth({ past: 0, future: 0 });
      currentRef.current = parsed.data;
      setLoadError(null);
      setSaveState("idle");
      setTimelineState(parsed.data);
    } catch (cause) {
      setLoadError((cause as Error).message);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const flush = useCallback(async () => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setSaveState("saving");
    try {
      await api.saveTimeline(projectId, next);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [projectId]);

  const scheduleSave = useCallback(
    (next: Timeline) => {
      pending.current = next;
      setSaveState("dirty");
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void flush(), AUTOSAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  /**
   * Applies a change. The result is validated first: an edit that would produce
   * an invalid timeline is rejected with a message rather than being stored and
   * breaking the render later.
   */
  const commit = useCallback(
    (mutate: (draft: Timeline) => Timeline) => {
      const current = currentRef.current;
      if (!current || suspended.current) return;

      const candidate = mutate(structuredClone(current));
      const parsed = safeValidateTimeline(candidate);
      if (!parsed.success) {
        setEditError(parsed.error.issues[0]?.message ?? "That change is not valid");
        return;
      }

      setEditError(null);
      past.current = [...past.current.slice(-HISTORY_LIMIT), current];
      future.current = [];
      currentRef.current = parsed.data;
      setDepth({ past: past.current.length, future: 0 });
      setTimelineState(parsed.data);
      scheduleSave(parsed.data);
    },
    [scheduleSave],
  );

  const undo = useCallback(() => {
    const current = currentRef.current;
    const previous = past.current[past.current.length - 1];
    if (!current || !previous || suspended.current) return;

    past.current = past.current.slice(0, -1);
    future.current = [current, ...future.current].slice(0, HISTORY_LIMIT);
    currentRef.current = previous;
    setDepth({ past: past.current.length, future: future.current.length });
    setTimelineState(previous);
    scheduleSave(previous);
  }, [scheduleSave]);

  const redo = useCallback(() => {
    const current = currentRef.current;
    const [next, ...rest] = future.current;
    if (!current || !next || suspended.current) return;

    future.current = rest;
    past.current = [...past.current.slice(-HISTORY_LIMIT), current];
    currentRef.current = next;
    setDepth({ past: past.current.length, future: future.current.length });
    setTimelineState(next);
    scheduleSave(next);
  }, [scheduleSave]);

  // Ctrl/Cmd+Z and Shift+Z, but never while the user is typing into a field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingInto(event.target)) return;
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") return;
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // A pending edit must not be lost to a tab close or a route change.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (pending.current) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      void flush();
    };
  }, [flush]);

  // Laid out at the rate the preview is mounted with, which need not be the
  // timeline's own — see the preview-rate control in the transport. Everything
  // in the editor reads this one layout, so the playhead, the timeline strip and
  // the Player cannot disagree about what a frame number means.
  const layout = useMemo(() => (timeline ? computeLayout(timeline, previewFps) : null), [timeline, previewFps]);

  return {
    timeline,
    layout,
    saveState,
    loadError,
    editError,
    commit,
    undo,
    redo,
    canUndo: depth.past > 0,
    canRedo: depth.future > 0,
    saveNow: flush,
    suspendEdits,
    /** Refetch after the server has rebuilt the timeline, e.g. after voice generation. */
    reload: load,
  };
}
